import { Button, ButtonVariant } from "components/Form/Button";
import { Input } from "components/Form/Input";
import { Layout } from "components/Layout";
import { memo, useCallback, useContext, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppRoutes } from "types";
import { ApiError, apiLogin } from "lib/api";
import { ReactComponent as Ai } from "assets/ai.svg";
import * as jose from "jose";
import { Field } from "components/Form/Field";
import { toast } from "react-toastify";
import { InputPassword } from "components/Form/InputPassword";
import { AuthContext } from "contexts/AuthContext";
import { Storage, StorageKey } from "lib/storage";

type FormValue = {
  email: string;
  password: string;
};

export const Login = memo(function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(false);
  const { setToken } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setError,
  } = useForm<FormValue>({
    defaultValues: {
      email: "",
      // The password must never be prefilled from the URL; a ?passphrase
      // param is the campaign passphrase, consumed on the welcome page.
      password: "",
    },
  });

  const onSubmit = useCallback<SubmitHandler<FormValue>>(
    async ({ email, password }) => {
      if (isValid) {
        setProcessing(true);

        try {
          const token = await apiLogin(email, password);

          if (token) {
            const payload = jose.decodeJwt(token);

            if (payload.registered) {
              Storage.set(StorageKey.TOKEN, token);
              setToken(token);
              // Keep query params (e.g. ?passphrase from QR deep links) so
              // the welcome page can prefill the campaign passphrase.
              navigate(
                {
                  pathname: AppRoutes.welcome,
                  search: searchParams.toString(),
                },
                { replace: true }
              );
              window.location.reload();
            } else {
              setError("email", {
                type: "custom",
                message:
                  "No ContributorID linked to this email. Check for typos or create a new ContributorID.",
              });
            }
          }
        } catch (err) {
          if (err instanceof ApiError) {
            if (err.attr) {
              setError(err.attr as "email" | "password", {
                type: "custom",
                message: err.message,
              });
            } else {
              toast.error("Unable to login, please try again later.");
            }
          }
        } finally {
          setProcessing(false);
        }
      }
    },
    [isValid, navigate, searchParams, setError, setToken]
  );

  const handleGoToSignup = useCallback(() => {
    // Clear any login token before going to signup
    Storage.remove(StorageKey.TOKEN);
    navigate(
      AppRoutes.signup +
        (searchParams.toString() ? `?${searchParams.toString()}` : "")
    );
  }, [navigate, searchParams]);

  return (
    <form className="contents" onSubmit={handleSubmit(onSubmit)}>
      <Layout
        className="h-full text-center"
        toolbar={
          <>
            <Button disabled={!isValid || processing} type="submit">
              {processing ? "Loading..." : "Log In"}
            </Button>

            <Button
              onClick={handleGoToSignup}
              variant={ButtonVariant.FLAT}
              type="button"
            >
              Create new ContributorID
            </Button>
          </>
        }
      >
        <div className="grid h-full">
          <div className="grid self-start justify-items-center">
            <Ai />

            <h1 className="text-24 leading-1.2 font-semibold mt-4">
              Access your ContributorID
            </h1>

            <p className="text-858494 mt-3">
              Please enter your email and password to access your ContributorID.
            </p>
          </div>

          <div className="text-left space-y-8">
            <Field
              label="Enter your email."
              description={errors.email?.message}
              descriptionClassName={
                errors.email?.message ? "text-fd380f" : undefined
              }
              as="label"
            >
              <Input
                disabled={processing}
                type="email"
                id="email"
                autoCapitalize="off"
                autoComplete="email"
                autoFocus
                {...register("email", { required: true })}
              />
            </Field>

            <Field
              label="Enter your password."
              description={errors.password?.message}
              descriptionClassName={
                errors.password?.message ? "text-fd380f" : undefined
              }
              as="label"
            >
              <InputPassword
                disabled={processing}
                type="password"
                id="password"
                autoCapitalize="off"
                autoComplete="current-password"
                {...register("password", { required: true })}
              />
            </Field>

            <div className="space-y-3 items-center">
              <p className="text-14 font-semibold text-858494 text-center text-decoration-line: underline">
                IMPORTANT NOTE
              </p>
              <p className="text-14 text-858494">
                Your password <strong>IS NOT THE CAMPAIGN PASSPHRASE</strong>.{" "}
                <br />
                If you don't have a password yet, click the Set up / reset
                password button.
              </p>
            </div>
            <div className="pt-8 flex justify-center">
              <Button
                to={{
                  pathname: AppRoutes.setPassword,
                  search: searchParams.toString(),
                }}
                variant={ButtonVariant.FLAT_BORDER}
                type="button"
                className="w-full max-w-xs"
              >
                Set up / reset password
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    </form>
  );
});
